(() => {
  // Measure whole carousel tracks, and individual rows in wrapping grids.
  // Do not truncate names, invent options, or change product/variant data.
  const gridSelector = '.product-grid';
  const managed = new Set();
  const observed = new Map();
  let frame;

  const visible = element => element && element.getClientRects().length && element.getBoundingClientRect().width > 0;
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(align);
  };
  const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(entries => {
    for (const entry of entries) {
      // Height changes are the result of alignment, not a reason to repeat it.
      const width = entry.contentRect.width;
      if (observed.get(entry.target) !== width) {
        observed.set(entry.target, width);
        schedule();
      }
    }
  });

  function reset(element) {
    element.removeAttribute('data-cv-align-part');
    element.style.removeProperty('--cv-natural-gap');
    element.style.removeProperty('--cv-align-gap');
  }

  function alignParts(rows, key) {
    // Batch all reads before writes, including across different product grids.
    const updates = [];
    for (const row of rows) {
      const parts = row.filter(card => visible(card[key])).map(card => ({
        element: card[key],
        top: card[key].getBoundingClientRect().top - card.item.getBoundingClientRect().top,
        margin: parseFloat(getComputedStyle(card[key]).marginBlockStart) || 0,
      }));
      const top = Math.max(...parts.map(part => part.top));
      if (parts.length < 2) continue;
      for (const part of parts) {
        const gap = top - part.top;
        if (gap > 0.5) updates.push({ ...part, gap });
      }
    }
    for (const { element, margin, gap } of updates) {
      element.style.setProperty('--cv-natural-gap', `${margin}px`);
      element.style.setProperty('--cv-align-gap', `${gap}px`);
      element.setAttribute('data-cv-align-part', key);
      managed.add(element);
    }
  }

  function align() {
    frame = null;
    managed.forEach(reset);
    managed.clear();
    const grids = [...document.querySelectorAll(gridSelector)];
    for (const grid of observed.keys()) {
      if (!grids.includes(grid)) { resize?.unobserve(grid); observed.delete(grid); }
    }
    const rows = [];
    for (const grid of grids) {
      if (!observed.has(grid)) { observed.set(grid, grid.getBoundingClientRect().width); resize?.observe(grid); }
      const cards = [...grid.querySelectorAll('.product-card-wrapper')].filter(wrapper => wrapper.closest(gridSelector) === grid).map(wrapper => {
        const card = wrapper.querySelector(':scope > .card');
        const information = card?.querySelector(':scope > .card__content > .card__information');
        const title = information?.querySelector(':scope > .card__heading');
        // Horizontal recommendations and image-less standard cards have a
        // different native layout; leave those layouts untouched.
        if (!card || card.classList.contains('card--horizontal') || !visible(title)) {
          wrapper.removeAttribute('data-cv-aligned');
          return null;
        }
        wrapper.setAttribute('data-cv-aligned', '');
        const swatches = information.querySelector('cv-card-swatches');
        return {
          item: wrapper.closest('.grid__item') || wrapper,
          title,
          price: information.querySelector('.card-information > .price'),
          variants: swatches || information.querySelector('.cv-card-option-summary'),
          choices: swatches?.querySelector('.cv-card-swatches__list'),
          summary: swatches && information.querySelector('.cv-card-option-summary'),
        };
      }).filter(Boolean);
      // offsetTop ignores Dawn's reveal/hover transforms and horizontal scroll.
      const gridRows = new Map();
      for (const card of cards) {
        const top = card.item.offsetTop;
        const key = [...gridRows.keys()].find(y => Math.abs(y - top) < 2) ?? top;
        if (!gridRows.has(key)) gridRows.set(key, []);
        gridRows.get(key).push(card);
      }
      rows.push(...gridRows.values());
    }
    // Each stage includes the space allocated by the previous stage. This also
    // handles optional vendor/ratings and two-line compare-at prices.
    for (const key of ['title', 'price', 'variants', 'choices', 'summary']) alignParts(rows, key);
  }

  function start() {
    const mutations = new MutationObserver(records => {
      if (records.some(record => {
        const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
        if (target?.closest(gridSelector)) return true;
        return [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 &&
          (node.matches(gridSelector) || node.querySelector(gridSelector)));
      })) schedule();
    });
    mutations.observe(document.body, { childList: true, subtree: true, characterData: true });
    // Filters, recommendations and theme-editor sections can replace cards.
    document.addEventListener('shopify:section:load', schedule);
    document.addEventListener('shopify:section:unload', schedule);
    document.addEventListener('load', event => {
      if (event.target.matches?.('link[rel="stylesheet"]') || event.target.closest?.(gridSelector)) schedule();
    }, true);
    window.addEventListener('resize', schedule, { passive: true });
    document.fonts?.ready.then(schedule);
    document.fonts?.addEventListener('loadingdone', schedule);
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
